"use client";

import React from "react";
import { Link } from "@/components/common/Link";
export default function PrivacyStatementPage() {
  return <main className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <section className="relative overflow-hidden">
        {}
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,var(--foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--foreground)_1px,transparent_1px)] [background-size:48px_48px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,var(--accent),transparent_30%),radial-gradient(circle_at_bottom_right,var(--accent),transparent_28%)] opacity-10" />

        <div className="relative mx-auto max-w-5xl px-6 py-20 md:px-8 md:py-24">
          {}
          <div className="mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-[var(--accent)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.34em] text-[var(--accent)]">
              Privacy
            </span>
          </div>

          {}
          <h1 className="font-sans font-bold text-5xl leading-[1] tracking-tighter text-[var(--foreground)] md:text-7xl">
            Privacy Statement
          </h1>

          <p className="mt-6 max-w-3xl font-serif text-lg leading-8 text-[var(--foreground)]/70">
            This Privacy Statement explains how N2N Capital collects, uses,
            shares, and safeguards personal data in connection with its websites,
            services, business relationships, events, and related activities.
          </p>

          {}
          <div className="mt-12 rounded-none border border-[var(--border)]/20 bg-[var(--card)] p-6 md:p-10 shadow-none">
            <div className="prose max-w-none prose-headings:font-sans prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-[var(--foreground)] prose-p:text-[var(--foreground)]/80 prose-p:leading-8 prose-strong:text-[var(--foreground)] prose-li:text-[var(--foreground)]/80 prose-li:leading-8">
              {}
              <h2 className="!mt-0 !mb-4 !text-3xl">
                N2N Capital Privacy Statement
              </h2>

              <p><strong data-ast-id="f:/src/pages/Privacy/sections/OurMissionValues.tsx|sig:wquism" data-ast-start="2261" data-ast-end="2293">Effective date:</strong> 1 April 2026</p>

              <p>
                N2N Capital and its affiliated companies (“N2N Capital”, “we”,
                “us”, or “our”) respect your privacy and are committed to
                appropriately protecting and managing Personal Data.
              </p>

              <p>
                This Privacy Statement (“Statement”) explains how we collect,
                use, share, and safeguard Personal Data in connection with:
              </p>

              <ul>
                <li>
                  Our websites, online portals, digital properties, and online
                  advertising or links on third‑party platforms and social media.
                </li>
                <li>
                  Existing or prospective business relationships, including when
                  we provide access to our offices, data centers, or other
                  facilities.
                </li>
                <li>
                  Your access to our marketing or promotional materials or your
                  participation in conferences, meetings, or events we host or
                  attend.
                </li>
                <li>
                  Employment, potential employment, or other engagements with us.
                </li>
              </ul>

              <p>
                This Statement also explains your choices and rights regarding
                such Personal Data.
              </p>

              <p>
                This Statement does not apply to Personal Data for which our
                customers are solely responsible as controllers, including data
                hosted in customer‑owned or customer‑controlled environments.
                That data is handled under the customer’s own policies and
                instructions.
              </p>

              <p>
                If you have any questions, comments, or concerns about this
                Statement or our privacy practices, please contact us using the
                details provided in the <em>How to Contact Us or Submit a
                Request</em> section below.
              </p>

              <p>
                Because we operate across multiple jurisdictions, this Statement
                must be read together with (a) the terms of your contract with us
                (if any); and (b) applicable local data‑protection and privacy
                laws (“Applicable Laws”). Where this Statement conflicts with
                Applicable Laws, we will comply with Applicable Laws.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                What is Personal Data?
              </h3>

              <p>
                “Personal Data” means any information relating to an identified
                or identifiable natural person (a “Data Subject”), such as a
                name, identification number, location data, online identifier,
                or factors specific to that person. In some jurisdictions,
                certain information about legal entities may also be treated as
                Personal Data.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Personal Data We Collect
              </h3>

              <p>
                We may collect Personal Data from you or about you directly,
                automatically, or from third parties, for example:
              </p>

              <p>
                <strong>Visitors to offices or facilities</strong>
              </p>
              <p>
                Full name, company or business association, contact details,
                identity documentation details, and CCTV images.
              </p>

              <p>
                <strong>
                  Visitors to secure facilities (e.g., data centers or secure
                  suites)
                </strong>
              </p>
              <p>
                First and last name, company, role or position, contact details,
                photograph, access credentials, and where permitted by law,
                certain biometric identifiers (such as hand or other
                access‑control scans) (“Biometrics”).
              </p>

              <p>
                <strong>Event participants</strong>
              </p>
              <p>
                Name, business affiliation, role, and contact details when you
                attend events, meetings, or conferences we host or join.
              </p>

              <p>
                <strong>Website and online users</strong>
              </p>
              <p>
                Technical data such as device identifiers, browser type,
                regional and language settings, IP address and approximate
                location, network provider, and usage data about our websites
                and online services, collected via cookies and similar
                technologies.
              </p>
              <p>
                Information from analytics and marketing providers (e.g.,
                campaign performance, site usage metrics).
              </p>

              <p>
                <strong>
                  Customers and prospective customers (and their
                  representatives)
                </strong>
              </p>
              <p>
                Name, role or position, business contact details, location
                information, account and login data, payment‑related
                information, and records of communications (email, phone,
                messaging, portals).
              </p>
              <p>
                Technical and usage data for our customer portals and online
                tools.
              </p>

              <p>
                <strong>Job applicants, employees, and consultants</strong>
              </p>
              <p>
                Identification and contact details, address, employment and
                education history, references, immigration/work‑authorization
                details, payroll and benefits information, performance and
                attendance information, and where applicable and permitted by
                law, certain health‑related data and/or background‑check
                information.
              </p>

              <p>
                <strong>Social media users</strong>
              </p>
              <p>
                Information available from your profile and interactions with our
                official accounts (e.g., account name, handle, profile content,
                posts in which we are tagged, and your communications with us).
              </p>

              <p>
                You may choose not to provide certain Personal Data; however, if
                that data is necessary to provide services or functionality, we
                may be unable to offer those services or functionalities.
              </p>

              <p>
                You may also voluntarily provide additional Personal Data (for
                example, feedback or survey responses). We will use that
                information only for the purposes for which it was provided or to
                improve our products and services.
              </p>

              <p>
                If you provide us with Personal Data about another person (for
                example, a colleague, employee, or family member), you confirm
                that you are authorized to do so and that you have informed that
                person of this Statement and obtained any necessary consents. You
                and your organization are responsible for any liability arising
                from such disclosure.
              </p>

              <p>
                We may also create or collect non‑identifiable or aggregated data
                that cannot be linked to an individual. Where such data cannot
                reasonably be used to identify a person, it is not treated as
                Personal Data under this Statement.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                How We Use Your Personal Data
              </h3>

              <p>
                We may use Personal Data for one or more of the following
                purposes:
              </p>

              <p>
                <strong>Providing and managing services and relationships</strong>
              </p>
              <ul>
                <li>
                  Negotiating, entering into, performing, and managing contracts
                  or other commercial arrangements.
                </li>
                <li>
                  Providing access to our offices, data centers, or other
                  facilities.
                </li>
                <li>
                  Operating our websites, portals, and online tools, including
                  customer and partner portals.
                </li>
                <li>
                  Delivering customer support, technical assistance, billing, and
                  account management.
                </li>
                <li>
                  Sending information about our products, services, market
                  insights, and company news (subject to your preferences and
                  Applicable Laws).
                </li>
              </ul>

              <p>
                <strong>Developing and improving products and services</strong>
              </p>
              <ul>
                <li>Enhancing our offerings and user experience.</li>
                <li>Conducting analytics, research, and quality assurance.</li>
              </ul>

              <p>
                <strong>Communications and relationship management</strong>
              </p>
              <ul>
                <li>Responding to your inquiries and requests.</li>
                <li>
                  Conducting satisfaction or sentiment surveys, where permitted.
                </li>
              </ul>

              <p>
                <strong>Business operations and marketing</strong>
              </p>
              <ul>
                <li>Planning, reporting, investor and stakeholder communications.</li>
                <li>
                  Marketing, prospecting, and business development, including
                  targeted outreach consistent with Applicable Laws.
                </li>
                <li>Human resources, recruiting, and talent management.</li>
              </ul>

              <p>
                <strong>Security, compliance, and risk management</strong>
              </p>
              <ul>
                <li>
                  Protecting our rights, property, operations, and the safety of
                  our employees, customers, and the public.
                </li>
                <li>Detecting and preventing fraud, abuse, and security incidents.</li>
                <li>Conducting internal audits, compliance reviews, and investigations.</li>
                <li>
                  Complying with legal and regulatory obligations, including
                  responding to lawful requests from authorities.
                </li>
              </ul>

              <p>
                Where we use automated decision‑making or AI‑based tools in a way
                that may significantly affect you, we will do so in compliance
                with Applicable Laws and, where required, provide appropriate
                notice and opportunities for human review or appeal.
              </p>

              <p>
                Certain jurisdictions may allow broader use of publicly available
                Personal Data or data provided on a voluntary basis; where
                permitted, we may rely on those bases.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Sharing and Disclosure of Personal Data
              </h3>

              <p>
                We may share Personal Data, to the extent permitted by Applicable
                Laws, with:
              </p>

              <ul>
                <li>
                  Affiliated entities in our group for centralized processing,
                  administration, and support.
                </li>
                <li>
                  Service providers and vendors who perform services for us (e.g.,
                  IT, hosting, security, analytics, marketing, HR, payroll) under
                  contracts that limit their use of Personal Data to our
                  instructions and require appropriate safeguards.
                </li>
                <li>
                  Partners, counterparties, or other third parties where you have
                  asked or authorized us to do so, or where sharing is reasonably
                  necessary in connection with a transaction or relationship.
                </li>
                <li>
                  Professional advisers (e.g., auditors, legal counsel,
                  consultants) to the extent required for their services.
                </li>
                <li>
                  Government authorities, regulators, law enforcement, or other
                  parties when required or permitted by law, to protect our
                  rights or the rights and safety of others, or in connection
                  with legal claims or investigations.
                </li>
                <li>
                  Third parties involved in corporate transactions, such as a
                  merger, acquisition, financing, reorganization, or sale of
                  assets or business, where Personal Data may be transferred as
                  part of the transaction.
                </li>
              </ul>

              <p>
                We may also share Personal Data for other purposes with your
                consent or as otherwise allowed under Applicable Laws.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Cross‑Border Transfers
              </h3>

              <p>
                Because N2N Capital may operate internationally, Personal Data
                may be transferred to and processed in countries other than the
                one in which it was originally collected, including countries
                that may have different data‑protection laws.
              </p>

              <p>
                Where we transfer Personal Data across borders, we will do so in
                accordance with Applicable Laws and will take appropriate measures
                to ensure a level of protection equivalent to that required in the
                originating jurisdiction, such as:
              </p>

              <ul>
                <li>
                  Contractual protections (including standard contractual clauses
                  where applicable).
                </li>
                <li>
                  Group‑level data‑transfer agreements and internal policies.
                </li>
                <li>Technical and organizational safeguards.</li>
              </ul>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Legal Bases for Processing (where applicable)
              </h3>

              <p>
                In jurisdictions that require a legal basis, we process Personal
                Data based on one or more of:
              </p>

              <ul>
                <li>
                  Performance of a contract with you or the organization you
                  represent.
                </li>
                <li>
                  Your consent, where you have expressly given it (for example,
                  to receive certain marketing communications). You may withdraw
                  consent at any time, subject to legal or contractual
                  restrictions.
                </li>
                <li>
                  Legitimate interests, such as improving our services,
                  maintaining security, preventing fraud, and managing our
                  business, provided these interests are not overridden by your
                  rights and interests.
                </li>
                <li>
                  Compliance with legal obligations, including regulatory
                  requirements and responding to lawful requests.
                </li>
                <li>Other bases permitted under Applicable Laws.</li>
              </ul>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                How We Protect Your Personal Data
              </h3>

              <p>
                We use reasonable and appropriate physical, technical, and
                organizational safeguards designed to protect Personal Data
                against accidental or unlawful destruction, loss, alteration,
                unauthorized disclosure, or access. These may include:
              </p>

              <ul>
                <li>Access controls, encryption, and network‑security measures.</li>
                <li>Backup, continuity, and recovery practices.</li>
                <li>
                  Periodic testing, assessments, and improvements of our security
                  measures.
                </li>
              </ul>

              <p>
                No system is completely secure, and we cannot guarantee absolute
                security of your Personal Data, especially for data transmitted
                over the internet. You are responsible for safeguarding any
                credentials and for using appropriate care when transmitting
                information to us.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                How Long We Retain Your Personal Data
              </h3>

              <p>
                We retain Personal Data only for as long as necessary to fulfill
                the purposes described in this Statement or as required or
                permitted by Applicable Laws, including:
              </p>

              <ul>
                <li>Providing services to you or the organization you represent.</li>
                <li>Performing or enforcing contracts.</li>
                <li>
                  Meeting legal, regulatory, accounting, or reporting obligations.
                </li>
                <li>
                  Protecting our rights, property, or safety and that of others.
                </li>
              </ul>

              <p>
                When Personal Data is no longer needed, we will delete or
                anonymize it in accordance with our policies and Applicable Laws.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Cookies and Other Tracking Technologies
              </h3>

              <p>
                Our websites may use cookies and similar tracking technologies to:
              </p>

              <ul>
                <li>Operate and secure the sites.</li>
                <li>
                  Understand usage patterns and improve performance and content.
                </li>
                <li>
                  Personalize your experience and, where permitted, deliver
                  relevant advertising.
                </li>
              </ul>

              <p>
                Depending on your jurisdiction, you may be presented with a cookie
                banner or preference tool that allows you to manage certain
                categories of cookies (other than strictly necessary cookies). You
                can also adjust your browser settings to block or delete cookies;
                however, this may affect site functionality.
              </p>

              <p>
                Where applicable, we will honor recognized browser‑based privacy
                signals, such as Global Privacy Control (GPC), in accordance with
                local law.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Your Rights
              </h3>

              <p>
                Depending on your location and Applicable Laws, you may have some
                or all of the following rights:
              </p>

              <ul>
                <li>
                  To know whether we process your Personal Data and for what
                  purposes.
                </li>
                <li>To access the Personal Data we hold about you.</li>
                <li>
                  To request correction of inaccurate or incomplete Personal Data.
                </li>
                <li>
                  To request information about how we share your Personal Data and
                  with whom.
                </li>
                <li>
                  Where processing is based on consent, to withdraw that consent.
                </li>
                <li>
                  To object to or request restriction of certain processing,
                  including direct marketing.
                </li>
                <li>
                  To request deletion of certain Personal Data (“right to be
                  forgotten”), subject to legal exceptions.
                </li>
                <li>To request data portability in certain circumstances.</li>
                <li>
                  To opt out of certain types of profiling or automated
                  decision‑making, where applicable.
                </li>
                <li>
                  To lodge a complaint with a relevant data‑protection authority.
                </li>
              </ul>

              <p>
                We may need to verify your identity before acting on a request and
                may decline requests where permitted by law (for example, where
                they would infringe the rights of others or conflict with legal
                obligations).
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Non‑Discrimination
              </h3>

              <p>
                We will not discriminate or retaliate against you for exercising
                your privacy rights in accordance with Applicable Laws. However,
                if your choices limit our ability to process necessary Personal
                Data, we may be unable to provide or continue some services to you
                or the organization you represent.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Children’s Privacy
              </h3>

              <p>
                Our services are not directed to children under the age of 18, and
                we do not knowingly collect Personal Data from children under 18.
                If you believe we have collected such data, please contact us so
                we can investigate and take appropriate action.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Third‑Party Websites and Services
              </h3>

              <p>
                Our websites and communications may contain links to third‑party
                websites, services, or content. We do not control and are not
                responsible for the privacy practices of such third parties. We
                encourage you to review their privacy policies before providing
                any Personal Data to them.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Business Transfers
              </h3>

              <p>
                If we enter into a transaction involving a transfer of some or all
                of our business or assets (such as a merger, acquisition, or
                restructuring), Personal Data may be shared with or transferred to
                the relevant third parties, subject to appropriate protections and
                in accordance with Applicable Laws.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                How to Contact Us or Submit a Request
              </h3>

              <p>
                If you have questions about this Statement, our privacy practices,
                or if you wish to exercise your rights under Applicable Laws, you
                may contact us at:
              </p>

              <p>
                <strong>Email:</strong>{" "}
                <Link to="mailto:Admin@n2ncapital.com" className="text-[var(--accent)] hover:underline">Admin@n2ncapital.pe</Link>
              </p>

              <p>
                <strong>Mail:</strong>
                <br />
                N2N Capital – Data &amp; Privacy Office
                <br />
                228 Hamilton Ave, 3rd Floor
                <br />
                Palo Alto, CA 94301
                <br />
                United States
              </p>

              <p>
                Where required by law, you may also have the right to contact your
                local data‑protection authority if you are not satisfied with our
                response.
              </p>

              {}
              <h3 className="!mt-10 !mb-3 !text-2xl">
                Changes to This Privacy Statement
              </h3>

              <p>
                We may update this Statement from time to time to reflect changes
                in our practices, technologies, legal requirements, or other
                factors. When we make material changes, we will update the
                “Effective date” at the top of this Statement and, where required,
                provide additional notice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>;
}